/**
 * <s-media-gallery> — main-photo gallery with a strip of selectable media.
 *
 * Commands (dispatched via commandfor/command on strip buttons):
 *   --show-media — shows the [data-media-panel] matching the button's data-media-id
 *                  and marks that button aria-pressed="true".
 *
 * Required markup:
 *   [data-media-panel="<id>"]                 — one main-spot panel per media; all but one carry `hidden`.
 *   button[command="--show-media"][data-media-id] — strip buttons targeting this element's id.
 *
 * Optional variant sync:
 *   data-product-id                           — on this element; matches the picker's product.
 *   script[type="application/json"][data-variant-media] — { "<variantId>": "<mediaId>" } map.
 *   Listens for the bubbling `s-variant-change` event ({ productId, variantId }) dispatched by
 *   <s-variant-picker> and shows that variant's assigned image. Variants without one keep the
 *   current image.
 */
class SMediaGallery extends HTMLElement {
  #ac;
  #variantMedia = {};

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('command', this.#handleCommand, opts);

    const map = this.querySelector('script[type="application/json"][data-variant-media]');
    if (map) {
      try {
        this.#variantMedia = JSON.parse(map.textContent) || {};
      } catch (error) {
        console.error('[s-media-gallery] Failed to parse variant media map:', error);
      }
      document.addEventListener('s-variant-change', this.#handleVariantChange, opts);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #handleCommand = (event) => {
    if (event.command !== '--show-media') return;
    const mediaId = event.source?.dataset.mediaId;
    if (!mediaId) return;
    this.#show(mediaId, true);
  };

  #handleVariantChange = (event) => {
    const { productId, variantId } = event.detail || {};
    if (productId && this.dataset.productId && String(productId) !== this.dataset.productId) return;
    const mediaId = this.#variantMedia[String(variantId)];
    if (mediaId) this.#show(mediaId);
  };

  #show(mediaId, fromStrip = false) {
    let found = false;
    for (const panel of this.querySelectorAll('[data-media-panel]')) {
      if (panel.dataset.mediaPanel === mediaId) found = true;
    }
    if (!found) return;

    for (const panel of this.querySelectorAll('[data-media-panel]')) {
      const isActive = panel.dataset.mediaPanel === mediaId;
      if (!isActive && !panel.hidden) {
        for (const video of panel.querySelectorAll('video')) video.pause();
      }
      panel.hidden = !isActive;
    }

    let activeButton = null;
    for (const button of this.querySelectorAll('button[command="--show-media"]')) {
      const isActive = button.dataset.mediaId === mediaId;
      button.setAttribute('aria-pressed', String(isActive));
      if (isActive) activeButton = button;
    }

    if (!activeButton) return;
    if (fromStrip) {
      activeButton.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      return;
    }
    // Variant changes scroll only the strip sideways, never the page, so the shopper stays at the picker.
    const strip = activeButton.parentElement;
    if (!strip) return;
    const offset = activeButton.offsetLeft - strip.offsetLeft;
    if (offset < strip.scrollLeft || offset + activeButton.offsetWidth > strip.scrollLeft + strip.clientWidth) {
      const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      strip.scrollTo({ left: offset, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  }
}

if (!customElements.get('s-media-gallery')) {
  customElements.define('s-media-gallery', SMediaGallery);
}
